/**
 * EditDrawer — rechts einfahrender Bearbeitungs-Drawer.
 *
 * Verhalten:
 *  - Slide-in von rechts, Overlay-Backdrop (halbtransparent).
 *  - Schließen via Backdrop-Klick, Escape-Taste, X-Button.
 *  - Body-Scroll-Lock solange offen.
 *  - Fokus-Management: erstes interaktives Element erhält Fokus beim Öffnen,
 *    Fokus kehrt zum Trigger zurück beim Schließen.
 *  - Responsive: Mobile (< 768px) → volle Breite; Desktop → 460px.
 *  - Dark-Mode-konform via getThemeColors.
 *  - i18n: Schließen-Button nutzt common.action.close.
 */
import React, { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getThemeColors } from '../../lib/theme';
import { useIsMobile } from '../../lib/useBreakpoint';

export interface EditDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  dark: boolean;
  children: React.ReactNode;
  width?: number | string;
}

export function EditDrawer({ open, onClose, title, dark, children, width }: EditDrawerProps) {
  const { t } = useTranslation();
  const tc = getThemeColors(dark);
  const isMobile = useIsMobile();
  const drawerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);

  // Body scroll lock + Escape handler — runs when drawer is mounted (open=true)
  useEffect(() => {
    // Remember the element that had focus before we opened the drawer so we can
    // restore it when it closes.
    triggerRef.current = document.activeElement;

    // Lock scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus first focusable child inside the drawer
    const focusTimer = setTimeout(() => {
      const focusable = drawerRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    }, 80);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      clearTimeout(focusTimer);
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prev;
      // Restore focus to the element that triggered the drawer.
      if (triggerRef.current && (triggerRef.current as HTMLElement).focus) {
        (triggerRef.current as HTMLElement).focus();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  const drawerWidth = isMobile ? '100%' : (width ?? 460);

  // Don't render anything when closed (avoids polluting ARIA tree with extra dialog roles)
  if (!open) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <div
        aria-hidden="true"
        onClick={handleBackdropClick}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9990,
          background: 'rgba(0,0,0,0.45)',
          transition: 'opacity 0.22s ease',
        }}
      />

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: drawerWidth,
          zIndex: 9991,
          background: tc.card,
          borderLeft: `1px solid ${tc.border}`,
          boxShadow: dark
            ? '-12px 0 40px rgba(0,0,0,0.6)'
            : '-12px 0 40px rgba(16,24,40,0.18)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'sh24-drawer-in 0.25s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: `1px solid ${tc.border}`,
          flexShrink: 0,
        }}>
          <h2 style={{
            margin: 0,
            fontSize: 15,
            fontWeight: 700,
            color: tc.heading,
            fontFamily: 'Outfit,sans-serif',
          }}>
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.action.close')}
            style={{
              background: 'none',
              border: `1px solid ${tc.border}`,
              borderRadius: 7,
              padding: '5px 12px',
              cursor: 'pointer',
              color: tc.muted,
              fontSize: 12,
              fontFamily: 'Outfit,sans-serif',
            }}
          >
            {t('common.action.close')}
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px',
        }}>
          {children}
        </div>
      </div>
    </>
  );
}
